import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import clinicInfo from '../config/clinicInfo';
import { useClinicProfile } from '../context/ClinicProfileContext';
import { buildTenantPath } from '../utils/tenantSlug';
import { buildWhatsAppUrl } from '../utils/contact';

const ContactPage = () => {
  const navigate = useNavigate();
  const { profile, effectiveTenantSlug, isTenantContext, isLoading } = useClinicProfile();

  const clinicDisplayName = isTenantContext
    ? profile?.displayName || 'a clínica'
    : clinicInfo.nomeFantasia;
  const fullAddress = isTenantContext
    ? profile?.fullAddress || ''
    : clinicInfo.endereco.enderecoCompleto;
  const shortAddressLine = isTenantContext
    ? profile?.addressLine1 || 'Endereco ainda nao configurado.'
    : `${clinicInfo.endereco.logradouro}, ${clinicInfo.endereco.numero}, ${clinicInfo.endereco.complemento}`;
  const cityAddressLine = isTenantContext
    ? profile?.addressLine2 || 'Consulte a recepcao para mais detalhes.'
    : `${clinicInfo.endereco.cidade}, ${clinicInfo.endereco.estado}`;
  const contactPhone = isTenantContext
    ? profile?.contactWhatsapp || profile?.contactPhone || ''
    : clinicInfo.contato.telefone;
  const contactWhatsappFull = isTenantContext
    ? profile?.contactWhatsappFull || profile?.contactPhoneFull || profile?.contactWhatsapp || profile?.contactPhone || ''
    : clinicInfo.contato.whatsappFull;
  const contactEmail = isTenantContext ? profile?.contactEmail || '' : clinicInfo.contato.email;
  const tenantHomePath = buildTenantPath('/', effectiveTenantSlug);

  const googleMapsUrl = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : '';
  const whatsappMessage = `Olá! Gostaria de mais informações sobre os serviços da ${clinicDisplayName}.`;
  const whatsappUrl = buildWhatsAppUrl(contactWhatsappFull, whatsappMessage);

  const handleWhatsAppClick = () => {
    if (!whatsappUrl) {
      toast.error('Este canal de contato está indisponível no momento.');
      return;
    }

    window.open(whatsappUrl, '_blank');
    setTimeout(() => {
      navigate(tenantHomePath);
    }, 1000);
  };

  const handleEmailClick = () => {
    if (!contactEmail) {
      toast.error('O contato por e-mail está indisponível no momento.');
      return;
    }

    window.location.href = `mailto:${contactEmail}?subject=${encodeURIComponent(`Contato - ${clinicDisplayName}`)}&body=${encodeURIComponent(whatsappMessage)}`;
    setTimeout(() => {
      navigate(tenantHomePath);
    }, 1000);
  };

  if (isTenantContext && isLoading && !profile) {
    return <p className="text-center py-12">Carregando dados da clínica...</p>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Entre em Contato</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {isTenantContext
              ? `Estamos aqui para ajudar. Entre em contato com ${clinicDisplayName} pelos canais abaixo.`
              : 'Estamos aqui para ajudar. Entre em contato conosco através de qualquer um dos meios abaixo.'}
          </p>
        </div>

        {/* Contact Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {googleMapsUrl ? (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow group"
            >
              <MapPin className="w-8 h-8 text-blue-600 mb-4 group-hover:text-blue-700" />
              <h3 className="text-lg font-semibold mb-2">Endereço</h3>
              <p className="text-gray-600">{shortAddressLine}</p>
              <p className="text-gray-600">{cityAddressLine}</p>
              <span className="text-sm text-blue-600 group-hover:text-blue-700 mt-2 inline-block">
                Ver no Google Maps
              </span>
            </a>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <MapPin className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Endereço</h3>
              <p className="text-gray-600">Endereço ainda não configurado.</p>
            </div>
          )}

          {whatsappUrl ? (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow group"
            >
              <Phone className="w-8 h-8 text-blue-600 mb-4 group-hover:text-blue-700" />
              <h3 className="text-lg font-semibold mb-2">Telefone (WhatsApp)</h3>
              <p className="text-gray-600">{contactPhone}</p>
              <span className="text-sm text-blue-600 group-hover:text-blue-700 mt-2 inline-block">
                Enviar mensagem
              </span>
            </a>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Phone className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Telefone (WhatsApp)</h3>
              <p className="text-gray-600">WhatsApp ainda não configurado.</p>
            </div>
          )}

          {contactEmail ? (
            <a
              href={`mailto:${contactEmail}`}
              className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow group"
            >
              <Mail className="w-8 h-8 text-blue-600 mb-4 group-hover:text-blue-700" />
              <h3 className="text-lg font-semibold mb-2">Email</h3>
              <p className="text-gray-600">{contactEmail}</p>
              <span className="text-sm text-blue-600 group-hover:text-blue-700 mt-2 inline-block">
                Enviar email
              </span>
            </a>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Mail className="w-8 h-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Email</h3>
              <p className="text-gray-600">E-mail ainda não configurado.</p>
            </div>
          )}

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <Clock className="w-8 h-8 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Horário</h3>
            {isTenantContext ? (
              <>
                <p className="text-gray-600">Disponibilidade sob consulta.</p>
                <p className="text-gray-600">Confirme horários pelo WhatsApp ou e-mail.</p>
              </>
            ) : (
              <>
                <p className="text-gray-600">{clinicInfo.horarios.semana}</p>
                <p className="text-gray-600">{clinicInfo.horarios.sabado}</p>
              </>
            )}
          </div>
        </div>

        {/* Contact Buttons */}
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6 text-center">Como prefere falar conosco?</h2>
          
          <div className="space-y-4">
            <button
              onClick={handleWhatsAppClick}
              disabled={!whatsappUrl}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
            >
              <Phone className="w-5 h-5 mr-2" />
              Enviar Mensagem via WhatsApp
            </button>

            <button
              onClick={handleEmailClick}
              disabled={!contactEmail}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
            >
              <Mail className="w-5 h-5 mr-2" />
              Enviar Email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
